// Settings card: record every raw CE060080 (multiplexed) packet for one erg
// session and download it as CSV (time, data ID, bytes in hex) for review.
import { useEffect, useRef, useState } from 'react';
import { Radio, Download, Square } from 'lucide-react';
import { toast } from 'sonner';
import {
  isRecordingRawMux, getRawMuxCount, getLastRawMux,
  startRawMuxRecording, stopRawMuxRecording, downloadRawMuxCsv,
} from '@/lib/rawMuxLogger';

export const RawMuxLogger = () => {
  const [running, setRunning] = useState(() => isRecordingRawMux());
  const [count, setCount] = useState(() => getRawMuxCount());
  const [last, setLast] = useState(() => getLastRawMux());
  const lastId = useRef(null);
  const [lastIdState, setLastIdState] = useState(null);

  useEffect(() => {
    const onTick = (e) => { setCount(e.detail?.count ?? 0); lastId.current = e.detail?.lastId; setLastIdState(e.detail?.lastId); };
    const onState = (e) => {
      setRunning(!!e.detail?.recording);
      if (!e.detail?.recording) { setLast(getLastRawMux()); }
    };
    window.addEventListener('crewz-mux-log-tick', onTick);
    window.addEventListener('crewz-mux-log-state', onState);
    return () => {
      window.removeEventListener('crewz-mux-log-tick', onTick);
      window.removeEventListener('crewz-mux-log-state', onState);
    };
  }, []);

  const start = () => {
    setLast(null); setCount(0); setLastIdState(null);
    startRawMuxRecording();
    setRunning(true);
    toast.success('Recording raw CE060080 packets — row your erg session, then Stop');
  };
  const stop = () => {
    const done = stopRawMuxRecording();
    setRunning(false);
    if (done && done.rows.length) {
      downloadRawMuxCsv(done.rows, done.startedAt);
      toast.success(`Saved ${done.rows.length} raw 0080 packets — attach the CSV in chat`);
    } else {
      toast('No CE060080 packets were received during the recording.');
    }
  };

  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3" data-testid="raw-mux-logger">
      <div className="flex items-center gap-2 mb-2">
        <Radio className="w-4 h-4 text-zinc-500" />
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Raw CE060080 log (erg session)</span>
      </div>
      <p className="text-xs text-zinc-400 mb-3">Records every raw multiplexed 0080 packet (time, data ID, bytes in hex). Start it, row one erg session, then Stop to download the CSV for review.</p>
      <div className="flex flex-wrap gap-2 items-center">
        {!running ? (
          <button type="button" onClick={start} className="px-3 py-1.5 text-xs font-bold rounded-md bg-zinc-900 text-white hover:bg-zinc-700 transition-colors" data-testid="raw-mux-start">Start recording</button>
        ) : (
          <button type="button" onClick={stop} className="px-3 py-1.5 text-xs font-bold rounded-md bg-red-600 text-white flex items-center gap-1 transition-colors" data-testid="raw-mux-stop"><Square className="w-3 h-3" /> Stop &amp; download ({count})</button>
        )}
        {!running && last && last.rows.length > 0 && (
          <button type="button" onClick={() => downloadRawMuxCsv(last.rows, last.startedAt)} className="px-3 py-1.5 text-xs font-bold rounded-md border border-zinc-300 flex items-center gap-1" data-testid="raw-mux-download"><Download className="w-3 h-3" /> Download again ({last.rows.length})</button>
        )}
      </div>
      {running && (
        <p className="mt-2 text-[11px] font-mono text-zinc-500" data-testid="raw-mux-live">
          {count} packets{lastIdState != null ? ` · last id 0x${Number(lastIdState).toString(16).padStart(2, '0')}` : ''}
        </p>
      )}
    </div>
  );
};
